import { Box, Typography, Button } from "@mui/material";
import {
  TaskAgentStateManager,
  CalendarEvent,
  PlanDetail,
} from "components/hooks/useTaskAgentState";
import TaskAgentContainer from "./TaskAgentContainer";
import {
  BookmarkSection,
  ChatMessageSection,
  ThinkingSection,
  FollowUpInputSection,
  AlertsSection,
} from "./TaskAgentSections";
import { PlanWithActionsSection } from "./PlanWithActionsSection";
import { CalendarEventsSection } from "components/calendar/CalendarEventsSection";
import { TasksSection } from "./TasksSection";
import { DocumentsSection } from "./DocumentsSection";
import { DelegationsSection } from "./DelegationsSection";
import AgentAlert from "./AgentAlert";
import AILoading from "components/common/AILoading";
import { RichContent } from "components/editor/utils";
import { Family } from "protogen/advisors_service_pb";

interface TaskAgentDisplayProps {
  stateManager: TaskAgentStateManager;
  familyRef?: string;
  family?: Family;
  agentThreadRef?: string;
  onUpdatePlanDetails?: (
    content: RichContent,
    details: PlanDetail[],
  ) => Promise<void>;
  taskRef?: string;
  onFollowUpQuestion?: (content: RichContent) => Promise<void>;
  onAddEvents?: (events: CalendarEvent[], calendarName: string) => void;
  onRetry?: () => void;
  onInterrupt?: () => void;
  isInterrupting?: boolean;
  currentActionType?: "updateClientDetails" | "followUpQuestion" | null;
  accountType?: "advisor" | "member";
  onDelegate?: (opportunityId: string) => Promise<void>;
  wrapInContainer?: boolean;
  onCreateDocument?: (content: string) => void;
  refreshTask?: () => Promise<void>;
  showDelegationActions?: boolean;
}

export default ({
  stateManager,
  familyRef,
  family,
  agentThreadRef,
  onUpdatePlanDetails,
  taskRef,
  onFollowUpQuestion,
  onAddEvents,
  onRetry,
  onInterrupt,
  isInterrupting = false,
  currentActionType = null,
  accountType = "advisor",
  onDelegate,
  wrapInContainer = true,
  onCreateDocument,
  refreshTask,
  showDelegationActions = true,
}: TaskAgentDisplayProps) => {
  const { state, computed, actions } = stateManager;
  const {
    isCurrentlyActive,
    generalFunctionCalls,
    visibleAlerts,
    planWithActions,
    unassociatedActions,
  } = computed;

  const handleUpdatePlanDetails = async (details: PlanDetail[]) => {
    if (!onUpdatePlanDetails) return;

    // Optimistically update the details in the local state immediately
    actions.updateState({
      todos: state.todos.map((todo) => ({
        ...todo,
        details: details,
      })),
    });

    // Interrupt the agent first if it's running
    if (state.isActive && onInterrupt) {
      await onInterrupt();
      // Wait longer for interrupt to complete and pending function calls to be cancelled
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }

    // Format details as a message
    const includedDetails = details.filter((d) => !d.isExcluded);
    const excludedDetails = details.filter((d) => d.isExcluded);

    const detailsList = includedDetails
      .map((d) => `- ${d.title}: ${d.value || "(not provided)"}`)
      .join("\n");

    let message = `Here are the updated plan details:\n${detailsList}`;

    if (excludedDetails.length > 0) {
      const excludedList = excludedDetails
        .map((d) => `- ${d.title}`)
        .join("\n");
      message += `\n\nThe following details are not useful for this task:\n${excludedList}`;
    }

    await onUpdatePlanDetails(
      {
        text: message,
        html: `<p>${message.replace(/\n/g, "<br>")}</p>`,
        json: JSON.stringify({
          type: "doc",
          content: [
            { type: "paragraph", content: [{ type: "text", text: message }] },
          ],
        }),
        attachments: [],
      },
      details,
    );
  };

  const hasContent = computed.hasContent;

  if (!hasContent) {
    return (
      <AILoading
        messages={currentActionType || "agent"}
        size="small"
        noIcon
        textAnimated
      />
    );
  }

  // Working on output message.
  // is active if all todos are done and function calls completed but no messages
  const isProcessingMessage =
    isCurrentlyActive &&
    state.todos.every((todo) => todo.status === "completed") &&
    generalFunctionCalls.every((fc) => fc.state === "completed");

  const showChatMessages =
    accountType !== "member" || state.clientOutputVisible;

  const maybeWrapped = (children: React.ReactNode) => {
    return wrapInContainer ? (
      <TaskAgentContainer
        title="Task Agent"
        isProcessing={false}
        processingSteps={[]}
        showProcessingAfterContent={false}
      >
        {children}
      </TaskAgentContainer>
    ) : (
      children
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      {maybeWrapped(
        <Box display="flex" flexDirection="column" gap="12px">
          {/* Bookmarks Section - Show tracked entities */}
          <BookmarkSection bookmarks={state.bookmarks} />

          {/* Thinking Section - Show when active, near top */}
          <ThinkingSection
            thinkingMessage={state.thinkingMessage}
            isActive={state.isActive}
          />

          {/* Combined Plan with Actions Section */}
          <PlanWithActionsSection
            planWithActions={planWithActions}
            isActive={state.isActive}
            unassociatedActions={unassociatedActions}
            onUpdatePlanDetails={handleUpdatePlanDetails}
            accountType={accountType}
            family={family}
            playbookRef={state.playbookRef}
            showActions={showChatMessages}
          />

          {/* Calendar Events Section - Show pending calendar events */}
          {state.calendarEvents.length > 0 && (
            <CalendarEventsSection
              calendarEventGroups={state.calendarEvents}
              familyRef={familyRef}
              agentThreadRef={agentThreadRef}
              onAddEvents={onAddEvents}
            />
          )}

          {/* Tasks Section - Show added tasks */}
          {state.addedTasks.length > 0 && (
            <TasksSection tasks={state.addedTasks} />
          )}

          {/* Documents Section - Show added documents */}
          {state.addedDocuments.length > 0 && (
            <DocumentsSection documents={state.addedDocuments} />
          )}

          {/* Chat Messages Section - Show for members, or for advisors only if client output is visible */}
          {showChatMessages && (
            <ChatMessageSection
              messages={state.chatMessages}
              functionCalls={generalFunctionCalls}
              isCurrentlyActive={isCurrentlyActive}
              onCreateDocument={onCreateDocument}
            />
          )}

          {/* Alerts Section - Show important alerts */}
          <AlertsSection
            alerts={visibleAlerts}
            onDismissAlert={actions.dismissAlert}
          />
          {/* Delegations Section - Show delegation opportunities */}
          {state.delegationOpportunities.length > 0 && (
            <DelegationsSection
              delegationOpportunityGroups={state.delegationOpportunities}
              agentThreadRef={agentThreadRef}
              taskRef={taskRef}
              familyRef={familyRef}
              onDelegate={onDelegate}
              refreshTask={refreshTask}
              showActions={showDelegationActions}
            />
          )}

          {state.hasFailed && (
            <AgentAlert
              severity="error"
              title="Task Agent Failed"
              action={
                onRetry ? (
                  <Button
                    variant="text"
                    size="small"
                    onClick={onRetry}
                    sx={{ mr: 1 }}
                  >
                    Keep trying
                  </Button>
                ) : undefined
              }
            />
          )}
          {/* Interrupt Button - Show at bottom when agent is active or interrupting */}
          {showChatMessages &&
            (state.isActive || isInterrupting) &&
            onInterrupt && (
              <Box>
                {!isInterrupting && (
                  <Typography
                    variant="bodyHeavy"
                    color="text.secondary"
                    sx={{
                      animation: "fadeIn 0.5s ease-in-out",
                      minHeight: "20px",
                    }}
                  >
                    {currentActionType && !isProcessingMessage
                      ? "Working through the details..."
                      : "Thinking about what to say..."}
                  </Typography>
                )}
                <Button
                  variant="text"
                  onClick={onInterrupt}
                  disabled={isInterrupting}
                  sx={{ height: "unset", fontSize: "11px" }}
                >
                  {isInterrupting
                    ? "Stopping..."
                    : "Headed the wrong direction? Click here to refine the work"}
                </Button>
              </Box>
            )}
          {/* Follow-up Input Section - Show when chat is complete and not interrupting */}
          {state.hasCompleted &&
            state.chatMessages &&
            !isCurrentlyActive &&
            onFollowUpQuestion &&
            showChatMessages &&
            !isInterrupting && (
              <FollowUpInputSection onFollowUpQuestion={onFollowUpQuestion} />
            )}
        </Box>,
      )}
    </Box>
  );
};
